import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  Clipboard,
  CreditCard,
  ExternalLink,
  FileSignature,
  FileText,
  Globe2,
  Inbox,
  LayoutDashboard,
  LockKeyhole,
  MailCheck,
  NotebookText,
  PencilRuler,
  SearchCheck,
  Settings,
  Sparkles,
  Target,
  Workflow,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminFormsDirectoryEntries } from "./adminFormConfigs";
import type { AdminFormsDirectoryEntry } from "./adminFormConfigs";
import { FormPortalHeader } from "./FormPortalHeader";

const businessOperations = [
  {
    label: "Leads",
    route: "/admin/leads",
    description: "Review website inquiry leads, captured intake details, and lead status history.",
    icon: Inbox,
  },
  {
    label: "Client Management",
    route: "/admin/clients",
    description: "Manage client records, project timelines, documents, notes, payments, and follow-up activities.",
    icon: Users,
  },
  {
    label: "Client Center",
    route: "/admin/truevance-client-center",
    description: "Master client records, brand-scoped history, contact records, and portal preparation.",
    icon: Users,
  },
  {
    label: "Project Center",
    route: "/admin/truevance-project-center",
    description: "Track brand-scoped project delivery, milestones, deliverables, payment status, and activity.",
    icon: NotebookText,
  },
  {
    label: "Proposal Dashboard",
    route: "/admin/proposal-dashboard",
    description: "Review and manage client proposals.",
    icon: LayoutDashboard,
  },
  {
    label: "Proposal Center",
    route: "/admin/truevance-proposal-center",
    description: "Manage proposal versions, approvals, acceptance, and client conversion for consulting engagements.",
    icon: FileText,
  },
  {
    label: "Executive Dashboard",
    route: "/admin/executive-dashboard",
    description: "Monitor revenue, lead source analytics, capacity, referrals, and command center KPIs.",
    icon: Sparkles,
  },
  {
    label: "Truevance Connect Business Center",
    route: "/admin/truevance-business-center",
    description: "Reusable business operations layer for consulting, assessment, and platform delivery.",
    icon: Workflow,
  },
  {
    label: "Consulting Pipeline",
    route: "/admin/consulting-pipeline",
    description: "Track discovery consultation, assessment, strategy, implementation, and ongoing support.",
    icon: BriefcaseBusiness,
  },
  {
    label: "Sales & Lead Center",
    route: "/admin/truevance-sales-center",
    description: "Manage lead intake, discovery calls, opportunities, assignments, and follow-ups.",
    icon: Target,
  },
  {
    label: "Workspace Provisioning Center",
    route: "/admin/workspace-provisioning-center",
    description: "Create, activate, brand, and govern shared workspace shells.",
    icon: Globe2,
  },
  {
    label: "Assessment Center",
    route: "/admin/truevance-assessment-center",
    description: "Structure assessments, findings, recommendations, and follow-up actions.",
    icon: SearchCheck,
  },
  {
    label: "Opportunity Management",
    route: "/admin/truevance-opportunities",
    description: "Persistent consulting opportunity records with stage tracking and follow-up dates.",
    icon: Workflow,
  },
  {
    label: "Assessment Records",
    route: "/admin/truevance-assessments",
    description: "Business assessment records for challenges, opportunities, recommendations, and follow-up actions.",
    icon: SearchCheck,
  },
  {
    label: "Consulting Assessments",
    route: "/admin/consulting-assessments",
    description: "Structured consulting assessments with scores, recommendations, and handoff-ready follow-up.",
    icon: SearchCheck,
  },
  {
    label: "Consulting Recommendations",
    route: "/admin/truevance-recommendations",
    description: "Linked recommendations that move an assessment toward an opportunity or roadmap.",
    icon: Sparkles,
  },
  {
    label: "Implementation Roadmaps",
    route: "/admin/implementation-roadmaps",
    description: "Quick wins, short-term projects, and long-term initiatives for consulting delivery.",
    icon: Workflow,
  },
  {
    label: "Consulting Projects",
    route: "/admin/truevance-projects",
    description: "Consulting project delivery records with phases, progress, revenue, and status.",
    icon: Users,
  },
  {
    label: "Assessment Framework",
    route: "/admin/assessment-framework",
    description: "Capture challenges, opportunities, processes, bottlenecks, recommendations, and solutions.",
    icon: SearchCheck,
  },
  {
    label: "Client Engagement Dashboard",
    route: "/admin/client-engagement-dashboard",
    description: "See active consulting clients, assessments in progress, and ongoing opportunities.",
    icon: Users,
  },
  {
    label: "Service Categories",
    route: "/admin/service-categories",
    description: "Organize consulting, digital solutions, automation, dashboard, and systems offers.",
    icon: PencilRuler,
  },
  {
    label: "Create Proposal",
    route: "/admin/proposals",
    description: "Create new proposals for selected services, packages, and add-ons.",
    icon: FileText,
  },
  {
    label: "Agreement Records",
    route: "/admin/agreements",
    description: "Review signed agreements and agreement status.",
    icon: FileSignature,
  },
  {
    label: "Payment Records",
    route: "/admin/payments",
    description: "Track payments, balances, and payment status.",
    icon: CreditCard,
  },
  {
    label: "Payment Settings",
    route: "/admin/payment-settings",
    description: "Configure Stripe tax, invoice defaults, deposits, and payment schedule preferences.",
    icon: Settings,
  },
  {
    label: "Scheduler Settings",
    route: "/admin/scheduler",
    description: "Manage scheduling links, kickoff calls, and scheduler requirements.",
    icon: CalendarDays,
  },
  {
    label: "Welcome Email Settings",
    route: "/admin/welcome-email-settings",
    description: "Manage onboarding and welcome email settings.",
    icon: MailCheck,
  },
];

const clientFormOrder = [
  "RSVP Hub Form",
  "Memory Hub Form",
  "Archive Hub Form",
  "Memory Media Vault Form",
  "Signature Song Form",
  "Signature Highlight Reel Form",
];

const clientServiceForms = clientFormOrder
  .map((name) => adminFormsDirectoryEntries.find((entry) => entry.formName === name))
  .filter((entry): entry is AdminFormsDirectoryEntry => Boolean(entry));

const memoryVaultAdminLinks = [
  { label: "Admin Dashboard", url: "https://vault.kreatethedate.com/admin" },
  { label: "Clients", url: "https://vault.kreatethedate.com/admin/clients" },
  { label: "Events / Vault Setup", url: "https://vault.kreatethedate.com/admin/events" },
  { label: "Media Review", url: "https://vault.kreatethedate.com/admin/media" },
  { label: "Storage Setup", url: "https://vault.kreatethedate.com/admin/storage-setup" },
  { label: "Activity Log", url: "https://vault.kreatethedate.com/admin/activity-log" },
  { label: "Settings", url: "https://vault.kreatethedate.com/admin/settings" },
  { label: "Admin User Manual", url: "https://vault.kreatethedate.com/admin/user-manual" },
];

const memoryVaultClientAccessLinks = [
  { label: "Client Login", url: "https://vault.kreatethedate.com/login" },
  { label: "Client Dashboard", url: "https://vault.kreatethedate.com/client/dashboard" },
  { label: "Pending Review", url: "https://vault.kreatethedate.com/client/pending" },
  { label: "Approved Gallery", url: "https://vault.kreatethedate.com/client/approved" },
  { label: "Featured Media", url: "https://vault.kreatethedate.com/client/featured" },
  { label: "Hidden / Private", url: "https://vault.kreatethedate.com/client/hidden" },
  { label: "Not Approved", url: "https://vault.kreatethedate.com/client/not-approved" },
  { label: "Upload Link Management", url: "https://vault.kreatethedate.com/client/upload-link" },
  { label: "Storage & Billing", url: "https://vault.kreatethedate.com/client/storage-billing" },
  { label: "Client Settings", url: "https://vault.kreatethedate.com/client/settings" },
  { label: "Client Instructions", url: "https://vault.kreatethedate.com/client/instructions" },
];

const futureExpansionItems = [
  "Partner Program",
  "Client Management",
  "Project Tracking",
  "Reporting",
  "Analytics",
  "ClientFlow Studio",
];

const supplementalGroups = [
  {
    title: "Internal Admin Forms",
    entries: adminFormsDirectoryEntries.filter((entry) => entry.category === "Admin Forms"),
  },
  {
    title: "Private Direct-Link Forms",
    entries: adminFormsDirectoryEntries.filter((entry) => entry.category === "Private Forms"),
  },
];

export function AdminFormsDirectoryPage() {
  const [copiedRoute, setCopiedRoute] = useState("");

  useEffect(() => {
    document.title = "Admin Command Center | Kreatethedate";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Private Kreatethedate Forms Portal operations headquarters and admin command center.");
  }, []);

  async function copyRoute(routeUrl: string) {
    const url = new URL(routeUrl, window.location.origin).toString();
    await navigator.clipboard.writeText(url);
    setCopiedRoute(routeUrl);
    window.setTimeout(() => setCopiedRoute((current) => (current === routeUrl ? "" : current)), 1800);
  }

  async function copyExternalUrl(url: string) {
    await navigator.clipboard.writeText(url);
    setCopiedRoute(url);
    window.setTimeout(() => setCopiedRoute((current) => (current === url ? "" : current)), 1800);
  }

  function renderInternalCard(item: { label: string; route: string; description: string; icon: typeof LayoutDashboard }) {
    const Icon = item.icon;

    return (
    <Link
      key={item.route}
      to={item.route}
      className="focus-ring group flex min-h-44 flex-col justify-between rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)]"
      title={`${item.label}: ${item.description}`}
    >
        <span className="flex items-start justify-between gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-[#050505] text-[#FF6A2B]">
            <Icon className="size-5" aria-hidden="true" />
          </span>
          <ArrowRight className="size-5 text-[#D94A16] transition group-hover:translate-x-1" aria-hidden="true" />
        </span>
        <span>
          <span className="block font-display text-2xl font-bold text-[#050505]">{item.label}</span>
          <span className="mt-2 block text-sm font-semibold leading-6 text-[#5c5246]">{item.description}</span>
          <span className="mt-3 block text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{item.route}</span>
        </span>
      </Link>
    );
  }

  function renderClientFormCard(entry: AdminFormsDirectoryEntry) {
    return (
    <article key={entry.routeUrl} className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <p className="font-display text-2xl font-bold text-[#050505]">{entry.formName}</p>
          <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">{entry.purpose}</p>
          <Link
            to={entry.routeUrl}
            className="focus-ring mt-3 inline-flex items-center gap-2 rounded-lg text-sm font-black text-[#050505] underline decoration-[#FF6A2B] decoration-2 underline-offset-4"
            title={`Open ${entry.formName}`}
          >
            {entry.routeUrl}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      <button
        type="button"
        onClick={() => void copyRoute(entry.routeUrl)}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
        title={`Copy ${entry.formName} route`}
      >
          {copiedRoute === entry.routeUrl ? <Check className="size-4" aria-hidden="true" /> : <Clipboard className="size-4" aria-hidden="true" />}
          {copiedRoute === entry.routeUrl ? "Copied" : "Copy Link"}
        </button>
      </article>
    );
  }

  function renderExternalShortcut(link: { label: string; url: string }) {
    return (
      <article key={link.url} className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <p className="font-display text-2xl font-bold text-[#050505]">{link.label}</p>
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="focus-ring mt-3 inline-flex items-center gap-2 rounded-lg text-sm font-black text-[#050505] underline decoration-[#FF6A2B] decoration-2 underline-offset-4"
            title={`Open ${link.label} in the Memory Media Vault`}
          >
            {link.url}
            <ExternalLink className="size-4" aria-hidden="true" />
          </a>
        </div>
      <button
        type="button"
        onClick={() => void copyExternalUrl(link.url)}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
        title={`Copy ${link.label} link`}
      >
          {copiedRoute === link.url ? <Check className="size-4" aria-hidden="true" /> : <Clipboard className="size-4" aria-hidden="true" />}
          {copiedRoute === link.url ? "Copied" : "Copy Link"}
        </button>
      </article>
    );
  }

  function renderSupplementalEntry(entry: AdminFormsDirectoryEntry) {
    return (
      <article
        key={entry.routeUrl}
        className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 md:grid-cols-[1.1fr_.8fr_.8fr_auto] md:items-center"
      >
        <div>
          <p className="font-display text-2xl font-bold text-[#050505]">{entry.formName}</p>
          <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">{entry.purpose}</p>
        </div>

        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">Route URL</p>
          <Link
            to={entry.routeUrl}
            className="focus-ring mt-1 inline-flex items-center gap-2 rounded-lg text-sm font-black text-[#050505] underline decoration-[#FF6A2B] decoration-2 underline-offset-4"
          >
            {entry.routeUrl}
            <ExternalLink className="size-4" aria-hidden="true" />
          </Link>
        </div>

        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">Public or Private</p>
          <p className="mt-1 text-sm font-black text-[#332a1f]">{entry.visibility}</p>
        </div>

        <button
          type="button"
          onClick={() => void copyRoute(entry.routeUrl)}
          className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
        >
          {copiedRoute === entry.routeUrl ? <Check className="size-4" aria-hidden="true" /> : <Clipboard className="size-4" aria-hidden="true" />}
          {copiedRoute === entry.routeUrl ? "Copied" : "Copy Link"}
        </button>
      </article>
    );
  }

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader />
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
            <LockKeyhole className="size-4" aria-hidden="true" />
            Private Admin Command Center
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">
            Kreatethedate Operations Headquarters
          </h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            A private command center for proposals, agreements, payments, scheduler tools, client service forms, and
            trusted shortcut access to the existing Memory Media Vault platform.
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              eyebrow="Section 1"
              title="Business Operations"
              count={`${businessOperations.length} workflow areas`}
              description="Move quickly through the core Kreatethedate proposal, agreement, payment, scheduler, and onboarding workflow."
            />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {businessOperations.map((item) => renderInternalCard(item))}
            </div>
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              eyebrow="Section 2"
              title="Client Service Forms"
              count={`${clientServiceForms.length} client forms`}
              description="Client-facing form links remain available here for admin review, copying, and quick access."
            />
            <div className="mt-5 grid gap-4">{clientServiceForms.map((entry) => renderClientFormCard(entry))}</div>
          </section>

          <section className="command-center-card rounded-lg p-5 text-[#fff9ef] sm:p-6">
            <SectionHeading
              eyebrow="Section 3"
              title="Memory Media Vault Admin"
              count={`${memoryVaultAdminLinks.length} external links`}
              description="External shortcut links only. These open the existing Memory Media Vault admin platform in a new tab."
              inverted
            />
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {memoryVaultAdminLinks.map((link) => renderExternalShortcut(link))}
            </div>
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              eyebrow="Section 4"
              title="Memory Media Vault Client Access"
              count={`${memoryVaultClientAccessLinks.length} external links`}
              description="External shortcut links for client review, approval, gallery, upload-link management, storage, and instructions."
            />
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {memoryVaultClientAccessLinks.map((link) => renderExternalShortcut(link))}
            </div>
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              eyebrow="Section 5"
              title="Future Expansion"
              count={`${futureExpansionItems.length} roadmap items`}
              description="Visual roadmap placeholders for future Kreatethedate operations growth. These cards are intentionally disabled."
            />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {futureExpansionItems.map((item) => (
                <article
                  key={item}
                  aria-disabled="true"
                  className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 text-[#fff9ef] opacity-78 transition hover:-translate-y-0.5 hover:border-[#FF6A2B]/50 hover:shadow-[0_18px_38px_rgba(45,32,8,.12)]"
                  title={`${item} roadmap placeholder`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-[#050505] text-[#FF6A2B]">
                      <Sparkles className="size-5" aria-hidden="true" />
                    </span>
                    <span className="rounded-full border border-[#FF6A2B]/28 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                      Coming Soon
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-bold text-[#fff9ef]">{item}</h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/72">
                    Roadmap placeholder for a future admin operations area.
                  </p>
                </article>
              ))}
            </div>
          </section>

          {supplementalGroups.map((group) =>
            group.entries.length ? (
              <section key={group.title} className="command-center-card rounded-lg p-5 sm:p-6">
                <SectionHeading
                  eyebrow="Additional Directory"
                  title={group.title}
                  count={`${group.entries.length} link${group.entries.length === 1 ? "" : "s"}`}
                  description="Additional private Forms Portal admin links retained for operational access."
                />
                <div className="mt-5 grid gap-4">{group.entries.map((entry) => renderSupplementalEntry(entry))}</div>
              </section>
            ) : null,
          )}
        </div>
      </section>
    </main>
  );
}

function SectionHeading({
  count,
  description,
  eyebrow,
  inverted = false,
  title,
}: {
  count: string;
  description: string;
  eyebrow: string;
  inverted?: boolean;
  title: string;
}) {
  return (
    <div className={`flex flex-col gap-2 border-b pb-4 sm:flex-row sm:items-end sm:justify-between ${inverted ? "border-[#FF6A2B]/18" : "border-[#FF6A2B]/14"}`}>
      <div>
        <p className={`text-xs font-black uppercase tracking-[0.18em] ${inverted ? "text-[#FF6A2B]" : "text-[#FF6A2B]"}`}>
          {eyebrow}
        </p>
        <h2 className={`mt-1 font-display text-3xl font-bold ${inverted ? "text-[#fff9ef]" : "text-[#fff9ef]"}`}>{title}</h2>
        <p className={`mt-3 max-w-4xl text-sm font-semibold leading-7 ${inverted ? "text-[#fff9ef]/70" : "text-[#fff9ef]/68"}`}>
          {description}
        </p>
      </div>
      <p className={`text-sm font-bold ${inverted ? "text-[#fff9ef]/70" : "text-[#fff9ef]/68"}`}>{count}</p>
    </div>
  );
}
